import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { ProductsAPI } from "../../../api/Products";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import {
  IProduct,
  ProductType,
  ThrivecartProduct,
} from "../../../generated/Api";
import { ITag } from "../../../types/TagTypes";
import Warning from "../../Modal/container/Warning";
import TagSelect from "../../TagSelect/TagSelect";
import { useModal } from "./../../Modal/Modal";

const ProductListCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 40px;
  grid-gap: 24px;
  align-items: flex-start;
  width: 100%;
`;

const ProductName = styled.div`
  font-size: 18px;
  line-height: 40px;
  color: #222222;
  font-weight: 400;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  width: 100%;
`;

const IconButton = styled.div`
  width: 40px;
  height: 40px;

  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition: all 0.6s;

  &:hover {
    background: #dc143c;
    transition: all 0.6s;

    > svg {
      color: #fff;
    }
  }

  > svg {
    color: #979797;
    width: 20px;
    height: 20px;

    transition: all 0.6s;
  }
`;

interface Props {
  product: IProduct;
  getAllIntegrationProducts: () => Promise<void>;
  cancelTagLoading?: boolean;
}

export function ProductItem(props: Props) {
  const { product, getAllIntegrationProducts } = props;
  const tagsPool = useRecoilValue(TagsAtom);
  const user = useRecoilValue(UserAtom);
  const modal = useModal();

  const [isLoading, setIsLoading] = useState(false);

  const productTags = useMemo(() => {
    return product.tagIds
      .map((pt) => tagsPool.find((t) => `${t._id}` === pt))
      .filter((t) => t);
  }, [product, tagsPool]);

  async function updateProductTags(tags: ITag[]) {
    setIsLoading(true);

    try {
      await ProductsAPI.update(product._id, {
        ...product,
        type: ProductType.THRIVECART,
        tagIds: tags.map((t) => `${t._id}`),
      });

      await getAllIntegrationProducts();
    } catch (err) {
      console.log(err);
      toast("An error occured when updating your product tags!", {
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteProduct() {
    setIsLoading(true);

    try {
      await ProductsAPI.remove(product._id);
      await getAllIntegrationProducts();
    } catch (err) {
      console.log(err);
      toast("An error occured when updating your product tags!", {
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  function confirmDeleteProduct() {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Delete Product"
        description="Are you sure you want to delete this product?"
        buttonsName={["No", "Yes"]}
        onOkClicked={async () => {
          try {
            deleteProduct();
          } catch (e) {
            console.log(e);
          } finally {
            modal.popModal();
          }
        }}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  return (
    <ProductListCard>
      <ProductName>{product.productName}</ProductName>

      <TagSelect
        cancelTagLoading={props.cancelTagLoading}
        tags={productTags}
        preserveSystemTags
        handleChoseTags={(tags) => updateProductTags(tags)}
      />

      <IconButton onClick={confirmDeleteProduct}>
        <FaTrashAlt />
      </IconButton>
    </ProductListCard>
  );
}
